import { addMinutes, isEqual, parseISO } from 'date-fns'

import { DAY_FIRST_HOUR } from '../../../../constants/date'
import {
  CALENDAR_GRID_MINUTES_PER_ROW,
  CalendarGridDropType,
} from '../../CalendarGrid.constants'
import {
  getCalendarGridDayTime,
  getCalendarGridItemDuration,
  getCalendarGridMinuteOffset,
} from '../../CalendarGrid.utils'

import {
  CALENDAR_GRID_DROP_SNAP_MIDPOINT,
  CALENDAR_GRID_DROP_SNAP_MINUTES,
} from './useCalendarGridDragDrop.constants'

function getIsCalendarGridTimeDropArea(data) {
  return data?.type === CalendarGridDropType.time
}

function getCalendarGridDropSlotMinutes({ data, minutes }) {
  const snapped =
    Math.ceil(
      minutes / CALENDAR_GRID_DROP_SNAP_MINUTES -
        CALENDAR_GRID_DROP_SNAP_MIDPOINT
    ) * CALENDAR_GRID_DROP_SNAP_MINUTES

  const windowStart = data.firstHour * CALENDAR_GRID_MINUTES_PER_ROW

  const lastStart =
    (data.firstHour + data.hourCount) * CALENDAR_GRID_MINUTES_PER_ROW -
    CALENDAR_GRID_DROP_SNAP_MINUTES

  return Math.min(Math.max(snapped, windowStart), lastStart)
}

function getCalendarGridDropStart({ event, locale, timezone }) {
  const data = event.over?.data.current

  const item = event.active.data.current?.item

  if (!getIsCalendarGridTimeDropArea(data) || !item?.start) {
    return null
  }

  const minutesPerPixel =
    (data.hourCount * CALENDAR_GRID_MINUTES_PER_ROW) / event.over.rect.height

  const startMinutes = getCalendarGridMinuteOffset({
    firstHour: DAY_FIRST_HOUR,
    locale,
    timezone,
    value: item.start,
  })

  const slotMinutes = getCalendarGridDropSlotMinutes({
    data,
    minutes: startMinutes + event.delta.y * minutesPerPixel,
  })

  const isSourceSlot =
    slotMinutes ===
    getCalendarGridDropSlotMinutes({ data, minutes: startMinutes })

  return getCalendarGridDayTime({
    day: data.day,
    minutes: isSourceSlot ? startMinutes : slotMinutes,
    timezone,
  })
}

export function getCalendarGridDropDay(event) {
  const data = event.over?.data.current

  return getIsCalendarGridTimeDropArea(data) ? null : (data?.day ?? null)
}

export function getCalendarGridDropPreviewItem({ event, locale, timezone }) {
  const item = event.active.data.current?.item

  const start = getCalendarGridDropStart({ event, locale, timezone })

  if (!item || !start || item.isAllDay) {
    return null
  }

  return {
    ...item,
    date: event.over.data.current.day,
    end: addMinutes(start, getCalendarGridItemDuration(item)).toISOString(),
    start: start.toISOString(),
  }
}

export function getIsCalendarGridPreviewItemUnchanged({
  currentItem,
  nextItem,
}) {
  const isSameStart = currentItem?.start === nextItem?.start

  const isSameDate = currentItem?.date === nextItem?.date

  return isSameStart && isSameDate
}

export function getIsCalendarGridPreviewItemMoved({ item, previewItem }) {
  const hasPreviewItem = Boolean(previewItem)

  return (
    hasPreviewItem &&
    !isEqual(parseISO(previewItem.start), parseISO(item.start))
  )
}

export function getIsCalendarGridItemMovedToDay({ day, item, sourceDay }) {
  const isDayDrop = Boolean(item && day && sourceDay)

  return isDayDrop && day !== sourceDay
}
